import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ReportHeader } from '../../header/report-header';
import { observer } from 'mobx-react-lite';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import { Chart } from 'primereact/chart';
import Colors from '../../../../utils/colors';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import { RealEstateSideBarFilter } from './side-bar-filter/real-estate-side-bar-filter';
import { RealEstateSideBarFiltersType } from './side-bar-filter/real-estate-side-bar-filters.type';
import { RealEstateReportType } from '../../../../api/types/reports/finanace/real-estate-report.type';
import { getReportRealEstateOperation } from './operations/get-report-real-estate-operation';

export const ReportRealEstateComparison = observer(() => {
  const [filters, setFilters] = useState<RealEstateSideBarFiltersType>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RealEstateReportType[]>();

  const fetchData = async (filters: RealEstateSideBarFiltersType) => {
    setFilters(filters);
    await getReportRealEstateOperation(filters, setData, setLoading);
  };

  // Transform building data to include utilization metrics
  const realEstateData = globalSettingsBranches.buildings.map((building) => {
    // Calculate mock utilization percentage based on building capacity
    const totalClasses = building.classes ? building.classes.length : 0;

    // Mock calculation - in real app this would come from actual usage data
    const utilizationPercentage = Math.floor(Math.random() * 101);

    return {
      buildingId: building.id,
      buildingName: building.buildingName,
      totalClasses,
      utilizationPercentage,
    };
  });

  const getUtilizationStyle = (percentage: number) => {
    if (percentage >= 90)
      return { color: Colors.approveText2, fontWeight: 'bold' };
    if (percentage >= 70)
      return { color: Colors.warningYelloText, fontWeight: 'bold' };
    return { color: Colors.dangerText2, fontWeight: 'bold' };
  };

  const chartData = {
    labels: realEstateData.map((item) => item.buildingName),
    datasets: [
      {
        label: 'אחוז ניצול',
        data: realEstateData.map((item) => item.utilizationPercentage),
        backgroundColor: realEstateData.map((item) => {
          if (item.utilizationPercentage >= 90) return Colors.approveText2;
          if (item.utilizationPercentage >= 70) return Colors.warningYelloText;
          return Colors.dangerText2;
        }),
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'אחוז ניצול',
        },
      },
      x: {
        title: {
          display: true,
          text: 'מבנה',
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    maintainAspectRatio: false,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח השוואת נדל״ן"
        description="דו״ח זה מציג השוואה של ניצול שטחי הנדל״ן בין המבנים השונים. הדו״ח מאפשר לנתח את יעילות השימוש במרחבי הלימוד ולזהות הזדמנויות לשיפור."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'ניתוח יעילות שימוש בנדל״ן' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="financial"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <RealEstateSideBarFilter handleSearch={fetchData} />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          {/* Chart Section */}
          <div
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
            }}
          >
            <Chart type="bar" data={chartData} options={chartOptions} />
          </div>

          {/* Table Section */}
          <DataTable
            value={data}
            rows={10}
            rowHover
            rowClassName={() => 'cursor-pointer'}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="buildingId"
            scrollable
            scrollHeight="calc(100vh - 466px)"
            tableStyle={{
              direction: 'rtl',
            }}
            loading={loading}
          >
            <Column
              field="buildingName"
              header="מבנה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="totalClasses"
              header="סה״כ כיתות"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="utilizationPercentage"
              header="אחוז ניצול"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData) => (
                <span
                  style={getUtilizationStyle(rowData.utilizationPercentage)}
                >
                  {`${rowData.utilizationPercentage}%`}
                </span>
              )}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
});
